'use client';

import { motion } from "framer-motion";
import Link from "next/link";
import { ArrowLeft, CheckCircle, MessageSquare, Bot, Users, Clock } from "lucide-react";
import { useParams } from "next/navigation";

const aiCustomerServiceData = {
  title: "AI-Powered Customer Service",
  subtitle: "Intelligent chatbots and automated support systems for enhanced customer experience.",
  headerGradient: "bg-gradient-to-r from-emerald-500 via-teal-600 to-cyan-600",
  accentColor: "text-emerald-600",
  buttonColor: "bg-emerald-600 hover:bg-emerald-700",
  challenge: "Companies struggle with providing 24/7 customer support while maintaining consistent service quality and managing operational costs. Traditional support channels often result in long wait times and inconsistent responses.",
  solutionHighlights: [
    "Deployed intelligent chatbots with natural language processing for instant customer responses",
    "Implemented automated ticket routing and prioritization based on urgency and complexity",
    "Created knowledge base integration for accurate and consistent information delivery",
    "Built sentiment analysis tools to identify customer satisfaction and escalation needs"
  ],
  benefits: [
    "90% reduction in average response time",
    "75% of inquiries resolved without human intervention",
    "50% improvement in customer satisfaction scores",
    "60% reduction in support operational costs"
  ],
  techStack: ["Natural Language Processing", "Machine Learning", "Chatbot Framework", "Sentiment Analysis", "API Integration", "Real-time Support"],
  serviceStats: {
    activeChats: "1,247",
    resolutionRate: "92%",
    avgResponseTime: "12s",
    satisfactionScore: "4.8/5"
  }
};

export default function AICustomerServicePage() {
  const params = useParams();
  const currentLocale = (params?.locale as string) || 'en';
  
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      {/* Header Section */}
      <header className={`py-16 md:py-24 text-white ${aiCustomerServiceData.headerGradient}`}>
        <div className="container mx-auto max-w-4xl px-4 text-center">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
            <Link href={`/${currentLocale}/portfolio`} className="inline-flex items-center text-sm text-white hover:text-emerald-100 transition-colors duration-300 bg-white/10 hover:bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full shadow-md">
              <ArrowLeft size={18} className="mr-2" />
              Back to Portfolio
            </Link>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            {aiCustomerServiceData.title}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-lg md:text-xl text-emerald-100 max-w-3xl mx-auto">
            {aiCustomerServiceData.subtitle}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-8 flex flex-wrap gap-3 justify-center">
            {aiCustomerServiceData.techStack.slice(0, 3).map(tech => (
              <span key={tech} className="bg-white/20 text-white px-3 py-1 rounded-full text-xs font-medium">
                {tech}
              </span>
            ))}
          </motion.div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="container mx-auto max-w-5xl px-4 py-12 md:py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white p-8 md:p-12 rounded-xl shadow-xl -mt-20 md:-mt-32 relative z-10">
          
          {/* Customer Service Dashboard Visual Section */}
          <section className="mb-12">
            <h2 className={`text-3xl font-semibold mb-8 text-center ${aiCustomerServiceData.accentColor}`}>AI Support Center</h2>
            <div className="bg-slate-100 p-4 sm:p-6 rounded-lg shadow-lg border border-slate-200">
              <div className="flex items-center justify-between px-4 py-2 mb-4 bg-gradient-to-r from-slate-700 to-slate-800 text-white text-sm font-semibold rounded-t-md shadow">
                <span>Customer Service Dashboard</span>
                <span className="text-xs bg-emerald-500/90 px-2 py-0.5 rounded-full flex items-center">
                  <Bot size={12} className="mr-1"/>
                  AI Active
                </span>
              </div>
              <div className="flex flex-col md:flex-row gap-4 min-h-[280px]">
                {/* Chat Interface Simulation */}
                <div className="flex-1 flex items-center justify-center bg-slate-200 rounded-md p-4 relative min-h-[200px]">
                  <div className="w-full h-full bg-white rounded-lg relative overflow-hidden shadow-inner border border-slate-300 flex flex-col">
                    <div className="bg-emerald-600 text-white px-4 py-2 text-sm font-medium">
                      <div className="flex items-center">
                        <MessageSquare size={16} className="mr-2" />
                        <span>AI Customer Support</span>
                        <div className="ml-auto flex items-center">
                          <div className="w-2 h-2 bg-green-400 rounded-full mr-1"></div>
                          <span className="text-xs">Online</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex-1 p-3 space-y-3 text-xs">
                      {/* Chat messages */}
                      <div className="flex items-start gap-2">
                        <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center">
                          <Bot size={12} className="text-emerald-600" />
                        </div>
                        <div className="bg-emerald-50 text-emerald-800 p-2 rounded-lg rounded-tl-none max-w-[80%]">
                          <p>Hello! How can I help you today?</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-2 justify-end">
                        <div className="bg-slate-100 text-slate-700 p-2 rounded-lg rounded-tr-none max-w-[80%]">
                          <p>I need help with my order status</p>
                        </div>
                        <div className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center">
                          <Users size={12} className="text-slate-600" />
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-2">
                        <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center">
                          <Bot size={12} className="text-emerald-600" />
                        </div>
                        <div className="bg-emerald-50 text-emerald-800 p-2 rounded-lg rounded-tl-none max-w-[80%]">
                          <p>I'd be happy to help! Can you provide your order number?</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 text-slate-500">
                        <div className="flex space-x-1">
                          <div className="w-1 h-1 bg-slate-400 rounded-full animate-bounce"></div>
                          <div className="w-1 h-1 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-1 h-1 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                        <span className="text-xs">AI is typing...</span>
                      </div>
                    </div>
                    
                    <div className="p-2 border-t border-slate-200">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Clock size={12} />
                        <span>Response time: 12s</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Sidebar Service Stats */}
                <div className="w-full md:w-52 bg-slate-50 rounded-md p-4 space-y-2.5 ring-1 ring-slate-200 flex flex-col justify-center">
                  {[ 
                    { label: "Active Chats", value: aiCustomerServiceData.serviceStats.activeChats, color: "text-emerald-700", bgColor:"bg-emerald-100", icon: MessageSquare }, 
                    { label: "Resolution Rate", value: aiCustomerServiceData.serviceStats.resolutionRate, color: "text-green-700", bgColor:"bg-green-100", icon: CheckCircle }, 
                    { label: "Avg Response Time", value: aiCustomerServiceData.serviceStats.avgResponseTime, color: "text-blue-700", bgColor:"bg-blue-100", icon: Clock }, 
                    { label: "Satisfaction Score", value: aiCustomerServiceData.serviceStats.satisfactionScore, color: "text-purple-700", bgColor:"bg-purple-100", icon: Users } 
                  ].map(stat => (
                    <div key={stat.label} className={`flex items-center justify-between p-2 rounded ${stat.bgColor}`}>
                      <div className="flex items-center">
                        <stat.icon size={14} className={`${stat.color} mr-1`} />
                        <span className={`text-xs font-medium ${stat.color}`}>{stat.label}</span>
                      </div>
                      <span className={`font-bold text-sm ${stat.color}`}>{stat.value}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="px-4 py-1.5 mt-3 bg-slate-200 border-t border-slate-300 text-xs text-slate-600 flex items-center gap-2 rounded-b-md">
                <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block animate-pulse"></span>
                <span>AI support system operational. Handling 247 concurrent conversations.</span>
              </div>
            </div>
          </section>

          <div className="grid md:grid-cols-1 gap-10">
            <section>
              <h2 className={`text-2xl font-semibold mb-4 ${aiCustomerServiceData.accentColor}`}>The Challenge</h2>
              <p className="text-slate-700 leading-relaxed whitespace-pre-line">
                {aiCustomerServiceData.challenge}
              </p>
            </section>

            <section>
              <h2 className={`text-2xl font-semibold mb-4 ${aiCustomerServiceData.accentColor}`}>Our Solution</h2>
              <ul className="space-y-3 text-slate-700">
                {aiCustomerServiceData.solutionHighlights.map((item, index) => (
                  <motion.li key={index} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.1 }} className="flex items-start pl-2">
                    <CheckCircle className={`w-5 h-5 ${aiCustomerServiceData.accentColor} mr-3 mt-0.5 flex-shrink-0`} />
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className={`text-2xl font-semibold mb-4 ${aiCustomerServiceData.accentColor}`}>Key Benefits</h2>
              <div className="bg-emerald-50 border border-emerald-200 p-6 rounded-lg shadow-sm">
                <ul className="space-y-3">
                  {aiCustomerServiceData.benefits.map((benefit, index) => (
                    <motion.li key={index} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.1 }} className="flex items-start text-emerald-800">
                      <CheckCircle className="w-5 h-5 text-emerald-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>{benefit}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </section>

            <section>
              <h2 className={`text-2xl font-semibold mb-5 ${aiCustomerServiceData.accentColor}`}>Technologies Used</h2>
              <div className="flex flex-wrap gap-3">
                {aiCustomerServiceData.techStack.map((tech, index) => (
                  <motion.span key={index} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: index * 0.05 }}
                    className="bg-slate-200 text-slate-700 px-4 py-1.5 rounded-full text-sm font-medium border border-slate-300 hover:bg-slate-300 transition-colors cursor-default">
                    {tech}
                  </motion.span>
                ))}
              </div>
            </section>
          </div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-16 text-center">
            <Link href={`/${currentLocale}/contact`} className={`${aiCustomerServiceData.buttonColor} text-white font-semibold py-3.5 px-10 rounded-lg text-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-opacity-50`}>
              Transform Your Customer Support
            </Link>
          </motion.div>

        </motion.div>
      </main>
    </div>
  );
} 